import type { HttpError } from '@refinedev/core'
import { useOne, useShow } from '@refinedev/core'
import type { Cabinet, Location } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import { Badge } from '~/components/ui/badge'
import {
  formatCredits,
  formatDateTime,
  formatOptionalDateTime,
} from '~/i18n/format'
import { History } from '~/pages/activity/history'
import { NotFoundPage } from '~/pages/notFound'
import { CabinetRounds } from '~/pages/rounds/cabinetRounds'
import { cabinetLanguageLabel } from './cabinetLanguage'
import { cabinetRefreshInterval } from './cabinetRefresh'
import { configurationRows } from './configurations'
import { CabinetCreditActions } from './creditActions'
import type { GameNames } from './gameNames'
import { gameNamesText } from './gameNames'
import { CabinetKeyAction } from './keyActions'
import { CabinetStatusAction, CabinetStatusBadge } from './statusActions'
import { useGameOptions } from './useGameOptions'

/**
 * The configuration of each of the cabinet's games: running, pending until
 * restart, or the one it starts on, each with when it was saved; "Assigned"
 * when one was picked for this cabinet (by staff or the tenant's
 * owners/admins), not Use default.
 */
function Configurations({
  cabinet,
  gameNames,
}: {
  cabinet: Cabinet
  gameNames: GameNames
}) {
  const { t } = useTranslation()
  const rows = configurationRows(cabinet.gameConfigs, gameNames, t)
  if (rows.length === 0) {
    return <>—</>
  }
  return (
    <ul
      aria-label={t('cabinetsPage.show.configurations')}
      className="space-y-1"
    >
      {rows.map((row) => (
        <li
          key={row.gameId}
          className="flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span>{row.summary}</span>
          {row.pending !== null && (
            <Badge variant="secondary">{row.pending}</Badge>
          )}
          {row.assigned && (
            <Badge variant="outline">{t('cabinetsPage.show.assigned')}</Badge>
          )}
        </li>
      ))}
    </ul>
  )
}

function LocationName({ id }: { id: string }) {
  const { t } = useTranslation()
  const { result, query } = useOne<Location, HttpError>({
    resource: 'locations',
    id,
  })
  if (query.isError) {
    return <>{t('cabinetsPage.show.locationLoadError')}</>
  }
  return (
    <Link
      className="underline"
      to={`/locations/${id}`}
    >
      {result?.name ?? '…'}
    </Link>
  )
}

export function CabinetShow() {
  const { t } = useTranslation()
  const { query } = useShow<Cabinet, HttpError>({
    resource: 'cabinets',
    queryOptions: {
      // its credits and `idle` follow play on the cabinet
      refetchInterval: (cabinetQuery) =>
        cabinetRefreshInterval(cabinetQuery.state),
    },
  })
  const cabinet = query.data?.data
  const { names: gameNames } = useGameOptions()

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  return (
    <ShowView>
      <ShowViewHeader
        title={cabinet?.name}
        actionsSlot={
          cabinet && (
            <>
              <CabinetKeyAction cabinet={cabinet} />
              <CabinetStatusAction cabinet={cabinet} />
            </>
          )
        }
      />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('cabinetsPage.common.loadError', {
            message: query.error.message,
          })}
        </p>
      )}
      {cabinet && (
        <div className="space-y-6">
          <dl className="grid max-w-md grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.name')}
            </dt>
            <dd>{cabinet.name}</dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.location')}
            </dt>
            <dd>
              {cabinet.locationId === null ? (
                t('cabinetsPage.common.inStorage')
              ) : (
                <LocationName id={cabinet.locationId} />
              )}
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.language')}
            </dt>
            <dd>
              {cabinetLanguageLabel(
                cabinet.language,
                cabinet.inheritedLanguage,
                t,
              )}
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.games')}
            </dt>
            <dd>{gameNamesText(cabinet.gameIds, gameNames)}</dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.configurations')}
            </dt>
            <dd>
              <Configurations
                cabinet={cabinet}
                gameNames={gameNames}
              />
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.status')}
            </dt>
            <dd>
              <CabinetStatusBadge status={cabinet.status} />
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.credits')}
            </dt>
            <dd>{formatCredits(cabinet.credits)}</dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.keyIssued')}
            </dt>
            <dd>
              {formatOptionalDateTime(cabinet.keyIssuedAt) ??
                t('cabinetsPage.common.never')}
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.lastSeen')}
            </dt>
            <dd>
              {formatOptionalDateTime(cabinet.lastSeenAt) ??
                t('cabinetsPage.common.never')}
            </dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.created')}
            </dt>
            <dd>{formatDateTime(cabinet.createdAt)}</dd>
            <dt className="text-muted-foreground">
              {t('cabinetsPage.show.updated')}
            </dt>
            <dd>{formatDateTime(cabinet.updatedAt)}</dd>
          </dl>
          <CabinetCreditActions cabinet={cabinet} />
          <CabinetRounds
            cabinet={cabinet}
            gameNames={gameNames}
          />
          <History
            record={{
              entity: 'cabinet',
              id: cabinet.id,
              tenantId: cabinet.tenantId,
            }}
            gameNames={gameNames}
          />
        </div>
      )}
    </ShowView>
  )
}
